import { useCallback, useEffect, useRef } from 'react';

/** 모바일 상세가 쌓은 방문 기록 칸의 표식 필드. 값은 어느 목록의 상세인지 가리키는 키다. */
export const MOBILE_DETAIL_STATE_KEY = 'mobileDetail';

function isOurEntry(key: string): boolean {
  const state: unknown = window.history.state;
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[MOBILE_DETAIL_STATE_KEY] === key
  );
}

// 같은 키를 쓰는 인스턴스가 둘 이상 마운트될 수 있다(팀 퀘스트 · 개발 퀘스트 창이 같은 칸을
// 본다). 둘 다 기록을 만지면 두 번 쌓거나 두 번 되돌려 앱 밖으로 나간다 — 먼저 붙은 하나만 만진다.
const owners = new Map<string, symbol>();

export interface UseMobileDetailHistoryOptions {
  /** 기록 칸 표식. 목록마다 달라야 한다(예: `quest:TEAM`). */
  key: string;
  /** 지금 상세가 목록을 덮고 있는가. */
  open: boolean;
  /** 기기 뒤로 가기 · 앞으로 가기 뒤 현재 칸에 맞춰 열림을 되돌려 준다. */
  onSync: (open: boolean) => void;
  /** 데스크톱처럼 목록·상세가 나란하면 false — 기록을 건드리지 않는다. */
  enabled?: boolean;
}

/**
 * 모바일 목록↔상세를 방문 기록에 묶는다. 상세가 열리면 칸 하나를 쌓아 Android 뒤로 버튼 ·
 * iOS Safari 가장자리 스와이프가 목록으로 돌아오게 하고, 다른 경로로 닫히면(‹ 버튼 · 시나리오
 * 전환 등) 그 칸을 걷는다. 열림 상태를 따라가므로 setter 를 하나하나 감쌀 필요가 없다.
 * 근거: docs/design/ui/scenario-ui.md §모바일 (history push).
 */
export function useMobileDetailHistory({
  key,
  open,
  onSync,
  enabled = true,
}: UseMobileDetailHistoryOptions): void {
  const idRef = useRef<symbol>(Symbol(key));
  const onSyncRef = useRef(onSync);
  const openRef = useRef(open);
  useEffect(() => {
    onSyncRef.current = onSync;
    openRef.current = open;
  });

  // 주인이 빠지면 남은 인스턴스가 다음 호출에서 넘겨받는다.
  const claim = useCallback((): boolean => {
    if (!owners.has(key)) owners.set(key, idRef.current);
    return owners.get(key) === idRef.current;
  }, [key]);

  useEffect(() => {
    if (!enabled) return;
    const id = idRef.current;
    const handlePopState = () => {
      if (owners.get(key) === id || !owners.has(key)) {
        owners.set(key, id);
        onSyncRef.current(isOurEntry(key));
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (owners.get(key) !== id) return;
      owners.delete(key);
      if (openRef.current && isOurEntry(key)) window.history.back();
    };
  }, [key, enabled]);

  useEffect(() => {
    if (!enabled || !claim()) return;
    if (open && !isOurEntry(key)) {
      const prev: unknown = window.history.state;
      const base = typeof prev === 'object' && prev !== null ? prev : {};
      window.history.pushState({ ...base, [MOBILE_DETAIL_STATE_KEY]: key }, '');
    } else if (!open && isOurEntry(key)) {
      window.history.back();
    }
  }, [key, open, enabled, claim]);
}
